import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: "edit_limit" | "build_limit" | "publish_limit" | "edit_denied" | "generic";
  currentTier?: "free" | "starter" | "pro";
}

const STARTER_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_STARTER || "";
const PRO_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_PRO || "";

const STARTER_FEATURES = [
  "Full reports on 10 starter ideas",
  "Up to 3 apps",
  "30 AI edits per month",
  "1 custom domain",
  "Email support",
];

const PRO_FEATURES = [
  "Full reports on all 157+ ideas",
  "Unlimited apps & deployments",
  "150 AI edits per month",
  "Unlimited custom domains",
  "Priority build queue",
  "Early access to agents",
];

const HEADLINES: Record<string, { title: string; subtitle: string }> = {
  edit_denied: {
    title: "Upgrade to use AI edits",
    subtitle: "Free accounts can browse ideas and build one app. Upgrade to unlock AI-powered edits.",
  },
  edit_limit: {
    title: "You've used all 30 Starter edits this month",
    subtitle: "Upgrade to Pro for 150 edits/month, or wait until your limit resets.",
  },
  build_limit: {
    title: "You've used your free build",
    subtitle: "Upgrade to keep building and launch your app to the world.",
  },
  publish_limit: {
    title: "Upgrade to publish your app",
    subtitle: "Free accounts can preview apps but need a paid plan to deploy.",
  },
  generic: {
    title: "Upgrade to unlock DayOne",
    subtitle: "Get unlimited builds, AI edits, custom domains, and more.",
  },
};

const CARD_STYLE: React.CSSProperties = {
  background: "rgba(255, 255, 255, 0.72)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.85)",
};

export function PaywallModal({
  isOpen,
  onClose,
  reason = "generic",
  currentTier = "free",
}: PaywallModalProps) {
  const [loadingTier, setLoadingTier] = useState<"starter" | "pro" | null>(null);

  const handleClose = useCallback(() => {
    if (!loadingTier) {
      onClose();
    }
  }, [loadingTier, onClose]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubscribe = async (tier: "starter" | "pro") => {
    const priceId = tier === "starter" ? STARTER_PRICE_ID : PRO_PRICE_ID;
    if (!priceId) {
      toast.error("Stripe is not configured. Contact support@usedayone.net");
      return;
    }

    setLoadingTier(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          priceId,
          successUrl: `${window.location.origin}/account?checkout=success`,
          cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=cancelled`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to create checkout session");
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("[PaywallModal] Checkout error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to start checkout");
      setLoadingTier(null);
    }
  };

  if (!isOpen) return null;

  const headline = HEADLINES[reason] || HEADLINES.generic;
  const showStarterCard = currentTier === "free";
  const showProCard = true; // Always show Pro

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        style={{ backdropFilter: "blur(4px)" }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl rounded-[24px] p-8"
        style={{
          background: "linear-gradient(135deg, #ede9f5 0%, #f5f0fa 100%)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={!!loadingTier}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-50"
        >
          <X className="h-5 w-5 text-[#6b5f8a]" />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2
            className="text-[28px] text-[#18151f] mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            {headline.title}
          </h2>
          <p
            className="text-[15px] text-[#6b5f8a] max-w-md mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {headline.subtitle}
          </p>
        </div>

        {/* Tier Cards */}
        <div className={`grid gap-6 ${showStarterCard ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-sm mx-auto"}`}>
          {/* Starter Card */}
          {showStarterCard && (
            <div className="rounded-[20px] p-6 flex flex-col" style={CARD_STYLE}>
              <div className="mb-5">
                <h3
                  className="text-[16px] font-semibold text-[#18151f] mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Starter
                </h3>
                <div className="flex items-baseline gap-1">
                  <span
                    className="text-[32px] text-[#18151f]"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    $29
                  </span>
                  <span
                    className="text-[14px] text-[#6b5f8a]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    /month
                  </span>
                </div>
              </div>

              <ul className="space-y-2.5 mb-6 flex-1">
                {STARTER_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#7c6af0] mt-0.5 flex-shrink-0" />
                    <span
                      className="text-[13px] text-[#18151f]"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe("starter")}
                disabled={!!loadingTier}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-[14px] font-medium text-white rounded-full transition-all disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, #a898f8, #cc82e8)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loadingTier === "starter" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  "Get Starter"
                )}
              </button>
            </div>
          )}

          {/* Pro Card */}
          {showProCard && (
            <div
              className="relative rounded-[20px] p-6 flex flex-col"
              style={{
                ...CARD_STYLE,
                border: "2px solid rgba(124, 106, 240, 0.5)",
                boxShadow: "0 8px 32px rgba(124, 106, 240, 0.15)",
              }}
            >
              <span
                className="absolute -top-3 right-4 px-3 py-1 text-[11px] font-medium text-white rounded-full"
                style={{ background: "linear-gradient(135deg, #a898f8, #cc82e8)" }}
              >
                Best value
              </span>

              <div className="mb-5">
                <h3
                  className="text-[16px] font-semibold text-[#18151f] mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Pro
                </h3>
                <div className="flex items-baseline gap-1">
                  <span
                    className="text-[32px] text-[#18151f]"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    $69
                  </span>
                  <span
                    className="text-[14px] text-[#6b5f8a]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    /month
                  </span>
                </div>
              </div>

              <ul className="space-y-2.5 mb-6 flex-1">
                {PRO_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#7c6af0] mt-0.5 flex-shrink-0" />
                    <span
                      className="text-[13px] text-[#18151f]"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe("pro")}
                disabled={!!loadingTier}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 text-[14px] font-medium text-white rounded-full transition-all disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, #a898f8, #cc82e8)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loadingTier === "pro" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  "Get Pro"
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p
          className="text-center text-[12px] text-[#6b5f8a] mt-6"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          Cancel anytime. No refunds — cancel before next billing cycle to stop charges.
        </p>
      </div>
    </div>
  );
}
